import './CbsaMap.scss';
import cn from 'classnames';
import { ReactNode, Ref, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSetError } from '../../redux/modals';
import { Placement } from '../designConstants';
import { Spinner } from '../Spinner';
import { NONCONTINENTAL_CBSAS } from './cbsa-map-constants';

type CBSAName = string;
type CBSAPath = string;
type CBSAPathMap = Record<CBSAName, CBSAPath>;

// Module-level promise cache — all CbsaMap instances share one network request per file.
let cbsaPathsPromise: Promise<CBSAPathMap> | null = null;
let statePathsPromise: Promise<CBSAPathMap> | null = null;

// `??=` won't reassign a rejected promise, so both fetchers below clear their cache on failure —
// that's what lets a retry issue a fresh network request instead of replaying the same rejection.
const fetchCbsaPaths = (): Promise<CBSAPathMap> => {
  cbsaPathsPromise ??= fetch('https://cdn.blisspointmedia.com/geo/CbsaPaths.json')
    .then(r => r.json())
    .catch(e => {
      cbsaPathsPromise = null;
      throw e;
    });
  return cbsaPathsPromise;
};

const fetchStatePaths = (): Promise<CBSAPathMap> => {
  statePathsPromise ??= fetch('https://cdn.blisspointmedia.com/geo/StatePaths.json')
    .then(r => r.json())
    .catch(e => {
      statePathsPromise = null;
      throw e;
    });
  return statePathsPromise;
};

interface CbsaMapProps {
  ref?: Ref<SVGSVGElement>;
  renderColor?: (cbsa: string) => string;
  renderTooltip?: (cbsa: CBSAName) => ReactNode;
  onPathLoad?: (loaded: boolean) => void;
}

export const CbsaMap = ({
  renderColor,
  renderTooltip = cbsa => cbsa,
  onPathLoad,
  ref
}: CbsaMapProps) => {
  const setError = useSetError();
  const [cbsaPaths, setCbsaPaths] = useState<CBSAPathMap>();
  const [stateOverlayPaths, setStateOverlayPaths] = useState<CBSAPathMap>();
  const [pathsLoading, setPathsLoading] = useState(false);
  // Without this, a persistent failure retries every render as soon as pathsLoading flips back
  // to false — an unbounded, no-backoff loop that re-hits the network and re-reports the same
  // error as fast as React can re-render, and the map never recovers.
  const [pathsLoadFailed, setPathsLoadFailed] = useState(false);

  useEffect(() => {
    if (cbsaPaths || pathsLoading || pathsLoadFailed) return;
    (async () => {
      setPathsLoading(true);

      try {
        // Data originally pulled from US Census at: https://www.census.gov/cgi-bin/geo/shapefiles/index.php?year=2023&layergroup=Core+Based+Statistical+Areas
        // And then processed with guidance from: https://medium.com/@mbostock/command-line-cartography-part-1-897aa8f8ca2c
        // And filtered out micropolitans which do not have corresponding data from TransUnion
        const [cbsaJSON, stateOverlayJSON] = await Promise.all([
          fetchCbsaPaths(),
          fetchStatePaths()
        ]);
        setCbsaPaths(cbsaJSON);
        setStateOverlayPaths(stateOverlayJSON);
      } catch (e) {
        setPathsLoadFailed(true);
        setError({
          message: `Failed to get cbsa path information: ${e.message}`,
          reportError: e
        });
      }

      setPathsLoading(false);
      if (onPathLoad) {
        onPathLoad(true);
      }
    })();
  }, [pathsLoading, cbsaPaths, pathsLoadFailed, setError, onPathLoad]);

  const cbsaMap = useMemo(
    () =>
      cbsaPaths && renderColor
        ? Object.keys(cbsaPaths).map(cbsa => (
            <OverlayTrigger
              key={cbsa}
              placement={Placement.BOTTOM}
              overlay={<Tooltip id='cbsa-tooltip'>{renderTooltip(cbsa)}</Tooltip>}
            >
              <path
                className={cn(
                  'cbsa-path',
                  { alaska: NONCONTINENTAL_CBSAS.ALASKA.includes(cbsa) },
                  { hawaii: NONCONTINENTAL_CBSAS.HAWAII.includes(cbsa) }
                )}
                d={cbsaPaths[cbsa]}
                fill={renderColor(cbsa)}
              />
            </OverlayTrigger>
          ))
        : null,
    [cbsaPaths, renderColor, renderTooltip]
  );

  const stateOverlayMap = useMemo(
    () =>
      stateOverlayPaths
        ? Object.keys(stateOverlayPaths).map(state => (
            <path
              className={cn(
                'state-path',
                { alaska: state === 'Alaska' },
                { hawaii: state === 'Hawaii' }
              )}
              key={state}
              d={stateOverlayPaths[state]}
              fill='transparent'
            />
          ))
        : null,
    [stateOverlayPaths]
  );

  return (
    <div className='cbsa-map'>
      {pathsLoading || !cbsaMap ? (
        <Spinner />
      ) : (
        <svg ref={ref} viewBox='390 85 420 270'>
          {cbsaMap}
          {stateOverlayMap}
        </svg>
      )}
    </div>
  );
};
